import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import { AlertCircle, Bot, Loader2, Lock, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin } from "@/lib/roles";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { automationApi } from "@/services/api/automation";
import type { SortDirection } from "@/hooks/useSortableData";
import { useServerPagination } from "@/hooks/useServerPagination";
import { useDebounce } from "@/hooks/useDebounce";
import { toast } from "sonner";

export const Route = createFileRoute("/portal/automation/agent-entitlements")({
  component: AgentEntitlements,
});

type SortField = "name" | "category" | "agent_type" | "enabled";
const PAGE_SIZES = [10, 25, 50, 100];

function formatCategoryName(category: string): string {
  return category
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : "Something went wrong";
}

function AgentEntitlements() {
  const { token, session } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const queryClient = useQueryClient();
  const canView = isSuperAdmin(session);

  const orgId = selectedOrganisation?.organisationId;
  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    offset,
    searchTerm,
    setSearchTerm,
    getPaginationInfo,
  } = useServerPagination({ initialItemsPerPage: 25 });

  const [category, setCategory] = useState<string>("all");
  const [sort, setSort] = useState<SortField>("name");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  // Debounce the search so typing doesn't fire a request (and a full-panel spinner) per keystroke.
  const debouncedSearch = useDebounce(searchTerm, 300);

  const params = {
    offset,
    limit: itemsPerPage,
    search: debouncedSearch || undefined,
    category: category === "all" ? undefined : category,
    sort,
    order,
  };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["agent-entitlements", orgId, params],
    queryFn: () => automationApi.agentEntitlements.list(token!, params),
    enabled: canView && !!token && !!orgId,
    placeholderData: keepPreviousData, // keep the current rows while the next page/filter loads
  });

  // The page operates on the SELECTED org — switching orgs must reset paging/filters, or a page or
  // category valid for org A dead-ends on org B (stale offset → empty grid with no way back).
  useEffect(() => {
    setCurrentPage(1);
    setCategory("all");
    setSearchTerm("");
  }, [orgId, setCurrentPage, setSearchTerm]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["agent-entitlements"] });

  const singleMutation = useMutation({
    mutationFn: (v: { agentType: string; isEnabled: boolean; name: string }) =>
      automationApi.agentEntitlements.update(token!, v.agentType, v.isEnabled),
    onSuccess: (_r, v) => {
      toast.success(`${v.isEnabled ? "Enabled" : "Disabled"} ${v.name}`);
      return invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const bulkMutation = useMutation({
    mutationFn: (v: { category: string; isEnabled: boolean }) =>
      automationApi.agentEntitlements.bulkUpdate(token!, {
        category: v.category,
        is_enabled: v.isEnabled,
      }),
    onSuccess: (r, v) => {
      toast.success(
        `${v.isEnabled ? "Enabled" : "Disabled"} ${r.updated_count} agent(s) in ${formatCategoryName(v.category)}`,
      );
      return invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const pending = singleMutation.isPending || bulkMutation.isPending;

  const total = data?.total ?? data?.total_count ?? 0;
  const items = data?.items ?? [];
  const { totalPages, startIndex, endIndex } = getPaginationInfo(total);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // Self-heal: if the current page landed past the end (e.g. rows removed, or an org with fewer
  // agents), snap back to page 1 rather than stranding the user on an empty page.
  useEffect(() => {
    if (!isFetching && total > 0 && items.length === 0 && currentPage > 1) {
      setCurrentPage(1);
    }
  }, [isFetching, total, items.length, currentPage, setCurrentPage]);

  const handleSort = (key: SortField) => {
    if (sort === key) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setOrder("asc");
    }
    setCurrentPage(1);
  };
  const sortDir = (key: SortField): SortDirection | null =>
    sort === key ? order : null;

  // Bulk applies to the whole selected category server-side. Only offer it for a real category and
  // NOT while a search is active — the visible rows would no longer equal the affected set.
  const bulkCategory =
    category !== "all" && category !== "uncategorised" && !searchTerm
      ? category
      : null;

  if (!canView) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="text-lg font-medium">Access Restricted</h3>
            <p className="mt-2 text-muted-foreground">
              Agent entitlement management is available to platform
              administrators only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-4xl font-bold text-slate-900">
            <Bot className="h-8 w-8" /> Agent Entitlements
          </h1>
          <p className="mt-2 flex flex-wrap items-center gap-2 text-lg text-slate-600">
            <span>
              Control which agents{" "}
              <span className="font-medium text-slate-900">
                {selectedOrganisation?.organisationName ?? "this organisation"}
              </span>{" "}
              sees on its dashboard.
            </span>
            {data?.is_platform_org && (
              <Badge variant="secondary">Platform org</Badge>
            )}
          </p>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search agents…"
            containerClassName="flex-1 min-w-[200px]"
          />
          <Select
            value={category}
            onValueChange={(val) => {
              setCategory(val);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {(data?.categories ?? []).map((c) => (
                <SelectItem key={c} value={c}>
                  {formatCategoryName(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {bulkCategory && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={pending}
                onClick={() =>
                  bulkMutation.mutate({ category: bulkCategory, isEnabled: true })
                }
              >
                Enable all
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pending}
                onClick={() =>
                  bulkMutation.mutate({
                    category: bulkCategory,
                    isEnabled: false,
                  })
                }
              >
                Disable all
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>

        {/* Table / states */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <div className="py-12 text-center">
            <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
            <h3 className="text-lg font-medium">
              Couldn’t load agent entitlements
            </h3>
            <p className="mt-2 text-muted-foreground">{errorMessage(error)}</p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center">
            <Bot className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="text-lg font-medium">
              {searchTerm || category !== "all"
                ? "No agents match your filters"
                : "No agents available for this organisation"}
            </h3>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    direction={sortDir("name")}
                    onSort={() => handleSort("name")}
                  >
                    Agent
                  </SortableTableHead>
                  <SortableTableHead
                    direction={sortDir("agent_type")}
                    onSort={() => handleSort("agent_type")}
                  >
                    Type
                  </SortableTableHead>
                  <SortableTableHead
                    direction={sortDir("category")}
                    onSort={() => handleSort("category")}
                  >
                    Category
                  </SortableTableHead>
                  <SortableTableHead
                    direction={sortDir("enabled")}
                    onSort={() => handleSort("enabled")}
                    className="w-[120px] text-right"
                  >
                    Entitled
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.agent_type}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.name}</span>
                        {item.is_internal && (
                          <Badge variant="destructive" className="gap-1">
                            <Lock className="h-3 w-3" /> Internal
                          </Badge>
                        )}
                        {!item.is_global && (
                          <Badge variant="outline">Org-owned</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {item.agent_type}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {item.category ? formatCategoryName(item.category) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.is_global ? (
                        <Switch
                          checked={item.is_enabled}
                          disabled={pending}
                          onCheckedChange={(checked) =>
                            singleMutation.mutate({
                              agentType: item.agent_type,
                              isEnabled: checked,
                              name: item.name,
                            })
                          }
                        />
                      ) : (
                        <Switch checked disabled aria-label="Always enabled" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {total > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  totalCount={total}
                  pageSize={itemsPerPage}
                  pageSizeOptions={PAGE_SIZES}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setItemsPerPage}
                  onFirstPage={() => setCurrentPage(1)}
                  onLastPage={() => setCurrentPage(totalPages)}
                  onPrevPage={() => setCurrentPage(currentPage - 1)}
                  onNextPage={() => setCurrentPage(currentPage + 1)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
